// Criação de projeto, compartilhada pelos DOIS caminhos que criam um: a página
// Projetos e o seletor no topo da barra lateral. Viver em um só lugar é o que
// impede o seletor de voltar a criar projeto sem perguntar o trilho — foi
// exatamente o que aconteceu quando a escolha morava só na página.
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, BadgeCheck, Info, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { apiFetch } from '@/lib/api';
import { type SignupConfig } from '@/lib/meta';
import { type Project } from '@/lib/projects';
import { useT } from '@/lib/i18n';

/**
 * Criação de projeto — e a escolha do trilho, que é PORTA DE MÃO ÚNICA.
 *
 * O modo é imutável depois de criado (trigger no banco), porque o trilho molda
 * o dado: histórico indexado por wamid contra id do whatsmeow, número de conta
 * comercial contra pareamento por QR, template que só existe no oficial. Trocar
 * no meio orfanaria a inbox. Por isso a tela diz isso ANTES, em vez de deixar o
 * cliente descobrir num erro depois.
 */
export function NewProjectDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (p: Project) => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  // Projeto novo nasce SÓ no trilho oficial: desde 2026-11 o trilho por QR code
  // não recebe projeto novo (a API recusa com unofficial_rail_closed). Os projetos
  // de QR que já existem seguem funcionando — só a porta de entrada fechou.
  const mode = 'OFFICIAL' as const;
  const [busy, setBusy] = useState(false);

  // ...MAS só quando o servidor tem como concluir a conexão. `api_mode` é
  // IMUTÁVEL: um projeto criado como oficial num ambiente sem as credenciais da
  // Meta nasce preso num trilho que não conecta, e não há como convertê-lo. Por
  // isso a escolha só aparece quando `GET /official/signup-config` diz enabled.
  // É também o que permite subir o trilho oficial desligado e ligá-lo depois
  // mexendo só em variável de ambiente, sem tocar no código.
  const [oficialDisponivel, setOficialDisponivel] = useState<boolean | null>(null);
  useEffect(() => {
    if (!open) return;
    let vivo = true;
    (async () => {
      const r = await apiFetch('GET', '/official/signup-config');
      const cfg = r.ok ? (r.data as SignupConfig) : null;
      if (!vivo) return;
      const pode = cfg?.enabled === true;
      setOficialDisponivel(pode);
    })();
    return () => { vivo = false; };
  }, [open]);

  const submit = async () => {
    if (!name.trim()) {
      toast.error(t('project.nameRequired'));
      return;
    }
    setBusy(true);
    try {
      const r = await apiFetch('POST', '/projects', { name: name.trim(), api_mode: mode });
      if (!r.ok) {
        toast.error(t('project.createFailed'));
        return;
      }
      onCreated(r.data as Project);
      onOpenChange(false);
      setName('');
    } finally {
      setBusy(false);
    }
  };

  // Enquanto o servidor não puder conectar a API oficial, não há trilho para
  // oferecer: a tela diz isso em vez de criar um projeto preso.
  const rails = oficialDisponivel
    ? [{ key: 'OFFICIAL' as const, icon: <BadgeCheck className="size-4" />, recommended: true }]
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('project.newTitle')}</DialogTitle>
          <DialogDescription>{t('project.newDesc')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <Label>{t('project.name')}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('project.namePlaceholder')}
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>{t('project.rail')}</Label>
            {rails.map(({ key, icon, recommended }) => (
              <button
                key={key}
                type="button"
                className={
                  'flex gap-3 rounded-lg border p-3 text-left transition-colors ' +
                  (mode === key ? 'border-primary bg-primary/5' : 'hover:bg-accent/50')
                }
              >
                <span
                  className={
                    'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ' +
                    (mode === key ? 'bg-primary/12 text-primary' : 'bg-muted text-muted-foreground')
                  }
                >
                  {icon}
                </span>
                <span className="min-w-0">
                  <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    {t(`project.rail${key}Title`)}
                    {recommended && (
                      <span className="rounded-full bg-primary/12 px-2 py-0.5 text-[11px] font-semibold text-primary">
                        {t('project.railRecommended')}
                      </span>
                    )}
                  </span>
                  <span className="block text-sm text-muted-foreground">
                    {t(`project.rail${key}Desc`)}
                  </span>
                </span>
              </button>
            ))}
            {oficialDisponivel === false && (
              <p className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-muted-foreground">
                <Info className="mt-0.5 size-4 shrink-0" /> {t('project.officialUnavailable')}
              </p>
            )}
            <p className="text-xs text-muted-foreground">{t('project.railImmutable')}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy || !name.trim() || !oficialDisponivel} onClick={submit}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t(mode === 'OFFICIAL' ? 'project.createAndSetup' : 'project.create')}
            {mode === 'OFFICIAL' && <ArrowRight className="size-4" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
